import { FC, useEffect, useRef, useState } from 'react';
import loadDynamicScript from '../loadDynamicScript';
import config from 'config/config';
import auth from '../auth/auth';
// import { useChatVisibility } from './useChatVisibility';
import { gql } from '@apollo/client';
import { client } from 'utils/apolloConnector/apolloConnector';

type Output = {
  loading: boolean;
  initialized: boolean;
  error?: Error;
};

const userContactsQuery = gql`
  query UserContacts {
    me {
      id
      firstName
      lastName
      email
      phone
    }
  }
`;

type UserContactsResponse = {
  me: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone?: string;
  };
};

export const useEmbeddedMessaging = (): Output => {
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const didInitRef = useRef(false);
  const didBindReadyListenerRef = useRef(false);

  useEffect(() => {
    const onReady = async () => {
      const bootstrap = window.embeddedservice_bootstrap;
      const setHidden = bootstrap?.prechatAPI?.setHiddenPrechatFields;

      if (!setHidden) return;

      const anonymous = !auth.isLoggedIn();

      // Always set at least your marker field
      const baseFields: Record<string, string> = {
        Chat_Name: config.SALESFORCE_EMBEDDED_MESSAGING_CHAT_NAME,
      };

      if (anonymous) {
        setHidden(baseFields);
        return;
      }

      try {
        const { data } = await client.query<UserContactsResponse>({
          query: userContactsQuery,
          fetchPolicy: 'network-only',
        });

        const me = data.me;
        const fullName = `${me.firstName ?? ''} ${me.lastName}`.trim();

        setHidden({
          ...baseFields,
          User_Id: me.id,
          User_FullName: fullName,
          User_Email: me.email || `${me.id}+noreply@fixly.pl`,
          User_Phone: me.phone ?? '',
        });
      } catch (e) {
        // If query fails, still set base fields so chat works
        console.error('Failed to fetch user contacts for prechat fields:', e);
        setHidden(baseFields);
      }
    };

    const run = async () => {
      // prevent double-init (React 18 StrictMode runs effects twice in dev)
      if (didInitRef.current) return;
      didInitRef.current = true;

      try {
        setLoading(true);

        // Bind listener BEFORE init, to not miss the event
        if (!didBindReadyListenerRef.current) {
          window.addEventListener('onEmbeddedMessagingReady', onReady);
          didBindReadyListenerRef.current = true;
        }

        // Load bootstrap script
        await loadDynamicScript(
          `${config.SALESFORCE_EMBEDDED_MESSAGING_SITE_URL}/assets/js/bootstrap.min.js`,
          'salesforce-embedded-messaging-bootstrap'
        );

        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) throw new Error('embeddedservice_bootstrap not found on window');

        // 2) Configure settings
        bootstrap.settings = {
          ...(bootstrap.settings ?? {}),
          language: config.LOCALE ?? 'pl_PL',
        };

        // 3) Init
        bootstrap.init(
          config.SALESFORCE_EMBEDDED_MESSAGING_ORG_ID,
          config.SALESFORCE_EMBEDDED_MESSAGING_DEPLOYMENT_NAME,
          config.SALESFORCE_EMBEDDED_MESSAGING_SITE_URL,
          {
            scrt2URL: config.SALESFORCE_EMBEDDED_MESSAGING_SCRT2_URL,
          }
        );

        setInitialized(true);
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    run();

    return () => {
      // remove listener on unmount
      if (didBindReadyListenerRef.current) {
        window.removeEventListener('onEmbeddedMessagingReady', onReady);
        didBindReadyListenerRef.current = false;
      }
    };
  }, []);

  return { loading, initialized, error };
};

export const Chat: FC = () => {
  // const isVisible = useChatVisibility();
  const { error } = useEmbeddedMessaging();
  if (error) console.error(error);
  return null;
};
